import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import GlassCard from "@/components/GlassCard";
import { motion } from "framer-motion";
import { Github, Mail, Lock, AlertCircle, Eye, EyeOff } from "lucide-react";
import { API_URL } from "@/lib/api";

function formatError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

function getOAuthErrorMessage(errorCode, provider) {
    switch (errorCode) {
        case "google_oauth_forbidden":
            return "Google sign-in is blocked by OAuth settings (403). Please verify approved redirect URIs in Google Cloud Console.";
        case "google_config_missing":
            return "Google sign-in is not configured on the server yet.";
        case "google_missing_code":
            return "Google sign-in did not return an authorization code. Please try again.";
        case "google_auth_failed":
            return "Google authentication failed. Please try again.";
        case "github_config_missing":
            return "GitHub sign-in is not configured on the server yet.";
        case "github_missing_code":
            return "GitHub sign-in did not return an authorization code. Please try again.";
        case "github_auth_failed":
            return "GitHub authentication failed. Please try again.";
        case "no_email":
            return provider === "google" ? "Could not get email from Google." : "Could not get email from GitHub.";
        default:
            return errorCode || "Authentication failed.";
    }
}

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { user, login, loginWithToken } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const location = useLocation();
    const redirectTo = location.state?.from?.pathname || "/genz-credit-scoring";

    // One navigation owner. Previously two effects raced: the OAuth handler sent
    // the user to their stashed return path while the `user` watcher sent them to
    // the default, and whichever landed second won.
    const oauthHandled = useRef(false);

    useEffect(() => {
        const token = params.get("token");
        const err = params.get("error");
        const provider = params.get("provider") || "";

        if (err) setError(getOAuthErrorMessage(err, provider));
        if (!token || oauthHandled.current) return;
        oauthHandled.current = true;

        loginWithToken(token).then(ok => {
            if (!ok) {
                setError(provider === "google"
                    ? "Google sign-in failed. Please try again."
                    : "Authentication failed. Please try again.");
                oauthHandled.current = false;
                return;
            }
            // The cross-origin provider redirect drops React Router state, so the
            // return path is stashed before the handoff.
            let stored = null;
            try {
                stored = sessionStorage.getItem("oauth_return_to");
                sessionStorage.removeItem("oauth_return_to");
            } catch { /* private mode */ }
            navigate(stored || redirectTo, { replace: true });
        });
    }, [params, loginWithToken, navigate, redirectTo]);

    useEffect(() => {
        // Skip while an OAuth token is still being exchanged, so this cannot
        // override the stashed return path.
        if (user && !params.get("token")) navigate(redirectTo, { replace: true });
    }, [user, navigate, redirectTo, params]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            const status = err.response?.status;
            if (!err.response) {
                setError("Connection error. Check your internet and try again.");
            } else if (status === 429) {
                setError("Too many failed attempts. Please wait 15 minutes before trying again.");
            } else if (status === 401) {
                setError("Invalid email or password. Please check your credentials.");
            } else {
                setError(formatError(err.response?.data?.detail) || "Login failed. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleOAuth = (provider) => {
        // Remember where the user was so we can return them there after OAuth.
        sessionStorage.setItem("oauth_return_to", redirectTo);
        window.location.href = `${API_URL}/api/oauth/${provider}/start`;
    };

    return (
        <div className="min-h-dvh flex items-center justify-center px-6 pt-20 pb-12">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md"
            >
                <div className="text-center mb-8">
                    <h1 className="font-heading text-4xl font-bold tracking-tighter text-text-primary mb-2" data-testid="login-heading">
                        Welcome back
                    </h1>
                    <p className="text-text-secondary text-base">Sign in to your GenZ Credit Score account</p>
                </div>

                <GlassCard className="p-8" hover={false}>
                    <button
                        onClick={() => handleOAuth("google")}
                        className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 border border-white/20 px-6 py-3 rounded-xl hover:bg-gray-50 hover:shadow-[0_0_15px_rgba(66,133,244,0.3)] transition-all font-medium text-sm mb-3"
                        data-testid="google-login-button"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Continue with Google
                    </button>

                    <button
                        onClick={() => handleOAuth("github")}
                        className="w-full flex items-center justify-center gap-3 bg-elevated text-text-primary border border-white/10 px-6 py-3 rounded-xl hover:bg-white/5 transition-all font-medium text-sm mb-6"
                        data-testid="github-login-button"
                    >
                        <Github size={18} />
                        Sign in with GitHub
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-xs text-text-secondary uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 bg-red-starter/10 border border-red-starter/20 rounded-xl px-4 py-3 mb-4" data-testid="login-error">
                            <AlertCircle size={16} className="text-red-starter shrink-0" />
                            <p className="text-sm text-red-starter">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                        <div>
                            <label htmlFor="login-email" className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-2 block">Email</label>
                            {/* glass-input-wrap: flex container so icon is a sibling, never overlaps text */}
                            <div className="glass-input-wrap">
                                <Mail size={16} className="field-icon" />
                                <input
                                    id="login-email"
                                    type="email"
                                    name="email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label htmlFor="login-password" className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold">Password</label>
                                <Link to="/forgot-password" className="text-[11px] text-blue-primary/80 hover:text-blue-primary transition-colors">
                                    Forgot password?
                                </Link>
                            </div>
                            <div className="glass-input-wrap">
                                <Lock size={16} className="field-icon" />
                                <input
                                    id="login-password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(s => !s)}
                                    className="eye-toggle"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    data-testid="login-password-toggle"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-blue-primary text-white font-medium px-6 py-3 rounded-xl hover:bg-blue-primary/90 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="login-submit-button"
                        >
                            {submitting ? "Signing in..." : "Sign In"}
                        </button>
                    </form>
                </GlassCard>

                <p className="text-center text-sm text-text-secondary mt-6">
                    Don&apos;t have an account?{" "}
                    {/* Bug 1: was "Create one" — tester asked for "Register Now" */}
                    <Link to="/register" className="text-blue-primary hover:text-blue-primary/80 transition-colors font-medium" data-testid="login-register-link">
                        Register Now
                    </Link>
                </p>
            </motion.div>
        </div>
    );
}
